/**
 * Dashboard Ring Calculations
 *
 * Core calculation logic for the three Whoop-style performance rings:
 * 1. Doorstroom (Velocity) - How well vehicles move through inventory
 * 2. Vandaag (Flow) - Whether today's work is under control
 * 3. Liquiditeit (Liquidity) - Cash safety: invoice aging vs ideal payment term
 *
 * SCOPE: Sales-only dealerships. Garage metrics are OUT OF SCOPE for v1.
 */

import { startOfDay, differenceInDays } from 'date-fns';
import type { DashboardRingSettingsType } from '@/features/settings/settings.schema';
import {
  type RingScore,
  type ExplanationItem,
  type PredictiveNudge,
  getRingColor,
} from '@/types/dashboardRings';
import type { RingItemStateMap } from '@/types/ringItemState';
import {
  getInvoiceReminderKey,
  getDoorstroomKey,
  getLiquiditeitKey,
  getQuoteFollowUpKey,
  getTaskKey,
  isItemHidden,
} from '@/types/ringItemState';

// DEV-only logger to satisfy eslint no-console without changing logic
const devLog = (...args: unknown[]) => {
  if (!import.meta.env.DEV) return;
  (globalThis as Record<string, unknown>)?.console?.log?.(...args);
};
const devWarn = (...args: unknown[]) => {
  if (!import.meta.env.DEV) return;
  (globalThis as Record<string, unknown>)?.console?.warn?.(...args);
};

// ============================================================================
// Input Types
// ============================================================================

export interface VehicleForRing {
  id: string;
  licensePlate: string;
  brand: string;
  model: string;
  status: string;
  /** Date vehicle entered inventory (purchaseDate or createdAt) */
  inStockSince: Date;
  /** Capital locked: costTotalExVat + bpmCents (cents) - for value-weighted scoring */
  capitalLockedCents?: number;
  /** Purchase price fallback if capitalLockedCents not available (cents) */
  purchasePriceCents?: number;
}

export interface SalesInvoiceForRing {
  id: string;
  invoiceNo: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  /** Customer/party name */
  partyName?: string;
  /** Invoice date */
  invoiceDate: Date;
  /** Due date for payment */
  dueDate?: Date;
  /** Date invoice was sent (for quote follow-up) */
  sentAt?: Date;
  /** Total amount in cents */
  totalCents: number;
  /** Is this being used as a quote? (draft status, sent to customer) */
  isQuote?: boolean;
  /** For quotes: has follow-up been dismissed? */
  followUpDismissed?: boolean;
  /** Invoice is marked ready to send (shows in Vandaag) */
  readyToSend?: boolean;
  /** Snoozed until this date (ISO string) - hidden from Vandaag until then */
  snoozedUntil?: string;
  /** Permanently dismissed from Vandaag */
  isDismissed?: boolean;
  /** Reminder snoozed until this date (for Liquiditeit) - hides from dashboard */
  reminderSnoozedUntil?: Date;
  /** Party email for sending reminders */
  partyEmail?: string;
  /** Party phone for WhatsApp */
  partyPhone?: string;
  /** Party/customer ID for fetching full customer data */
  partyId?: string;
}

export interface ManualTaskForRing {
  id: string;
  title: string;
  status: 'open' | 'done';
  dueAt?: Date;
  completedAt?: Date;
  /** Snoozed until this date (ISO string) - hidden from Vandaag until then */
  snoozedUntil?: string;
  /** Permanently dismissed from Vandaag */
  isDismissed?: boolean;
  /** Task scope: 'team' for team tasks, 'user' for personal tasks */
  scope: 'team' | 'user';
}

/**
 * Weekly goals data for Vandaag v2
 * These contribute 30% to the Vandaag score
 */
export interface WeeklyGoalsData {
  /** Number of test drives this week (agenda items with type = proefrit) */
  testDrivesCompleted: number;
  testDrivesTarget: number;
  /** Number of new leads this week (customers created) */
  newLeadsCount: number;
  newLeadsTarget: number;
  /** Number of follow-ups completed this week */
  followUpsCompleted: number;
  followUpsTarget: number;
}

export interface RingCalculationInput {
  vehicles: VehicleForRing[];
  salesInvoices: SalesInvoiceForRing[];
  manualTasks: ManualTaskForRing[];
  settings: DashboardRingSettingsType;
  /** Total inventory value in cents (for Liquiditeit exposure calculation) */
  inventoryValueCents?: number;
  /** Weekly goals data for Vandaag v2 (optional - if not provided, only daily actions count) */
  weeklyGoals?: WeeklyGoalsData;
  /** Combined ring state map (team + user) for filtering */
  ringStateMap?: RingItemStateMap;
}

export interface RingCalculationResult {
  rings: {
    doorstroom: RingScore;
    vandaag: RingScore;
    liquiditeit: RingScore;
  };
  explanations: ExplanationItem[];
  nudges: PredictiveNudge[];
}

// ============================================================================
// Doorstroom (Velocity) Calculation
// ============================================================================

/** Default fallback value for vehicles without capital locked data (€10,000 in cents) */
const DEFAULT_VEHICLE_VALUE_CENTS = 1_000_000;

interface VehicleHealth {
  vehicle: VehicleForRing;
  ageDays: number;
  healthScore: number;
  /** Vehicle value in cents for weighting (capitalLockedCents or fallback) */
  valueCents: number;
  /** Impact for sorting: (100 - healthScore) * valueCents */
  impact: number;
}

/**
 * Get vehicle value for weighting (in cents)
 * Priority: capitalLockedCents > purchasePriceCents > DEFAULT_VEHICLE_VALUE_CENTS
 * Note: Fallbacks are expected for vehicles without purchase invoices - no warnings needed
 */
function getVehicleValueCents(vehicle: VehicleForRing): number {
  if (vehicle.capitalLockedCents && vehicle.capitalLockedCents > 0) {
    return vehicle.capitalLockedCents;
  }
  if (vehicle.purchasePriceCents && vehicle.purchasePriceCents > 0) {
    return vehicle.purchasePriceCents;
  }
  return DEFAULT_VEHICLE_VALUE_CENTS;
}

/**
 * Calculate health score for a single vehicle based on age
 * - age <= staleVehicleDays: 100%
 * - age >= criticalVehicleDays: 0%
 * - Linear decay between those points
 */
function calculateVehicleHealth(
  vehicle: VehicleForRing,
  now: Date,
  settings: DashboardRingSettingsType
): VehicleHealth {
  const ageDays = differenceInDays(now, vehicle.inStockSince);
  const { staleVehicleDays, criticalVehicleDays } = settings;

  let healthScore: number;
  if (ageDays <= staleVehicleDays) {
    healthScore = 100;
  } else if (ageDays >= criticalVehicleDays) {
    healthScore = 0;
  } else {
    // Linear decay between stale and critical
    const range = criticalVehicleDays - staleVehicleDays;
    const overStale = ageDays - staleVehicleDays;
    healthScore = Math.max(0, 100 - (overStale / range) * 100);
  }

  // Get vehicle value for weighting
  const valueCents = getVehicleValueCents(vehicle);

  // Impact = how much this vehicle drags down the score (for sorting)
  // Higher value = more impactful
  const impact = (100 - healthScore) * valueCents;

  return {
    vehicle,
    ageDays,
    healthScore,
    valueCents,
    impact,
  };
}

/**
 * Calculate Doorstroom (Velocity) ring score
 *
 * SCORING MODEL (v2):
 * - Count-weighted health (70%): avg(healthScore) across all vehicles
 * - Value-weighted health (30%): sum(healthScore * valueCents) / sum(valueCents)
 * - Final score = round(countWeighted * 0.7 + valueWeighted * 0.3)
 *
 * COLOR THRESHOLDS:
 * - green: >= 70
 * - orange: 40-69
 * - red: < 40
 *
 * TWEAK POINTS:
 * - staleVehicleDays / criticalVehicleDays: in DashboardRingSettings
 * - Count/value weights: constants below (0.7 / 0.3)
 * - DEFAULT_VEHICLE_VALUE_CENTS: fallback when no purchase data
 */
const DOORSTROOM_COUNT_WEIGHT = 0.7;
const DOORSTROOM_VALUE_WEIGHT = 0.3;

/**
 * Calculate Doorstroom (Velocity) ring score
 *
 * TRUTH METRIC: Score is calculated from ALL vehicles, not filtered.
 * Hiding/snoozing/muting only affects the explanation list, NOT the score.
 *
 * @param allVehicles - ALL active vehicles (for score calculation - truth metric)
 * @param settings - Dashboard ring settings
 * @param visibleVehicles - Optional filtered vehicles (for explanations only)
 */
export function calculateDoorstroom(
  allVehicles: VehicleForRing[],
  settings: DashboardRingSettingsType,
  visibleVehicles?: VehicleForRing[]
): { score: RingScore; explanations: ExplanationItem[]; nudges: PredictiveNudge[] } {
  const now = startOfDay(new Date());

  // Filter to active vehicles only (available, reserved, purchased - NOT sold)
  const activeVehicles = allVehicles.filter(
    (v) => v.status === 'available' || v.status === 'reserved' || v.status === 'purchased'
  );

  // For explanations, use visible vehicles if provided, otherwise use all
  const vehiclesForExplanations = visibleVehicles
    ? visibleVehicles.filter(
        (v) => v.status === 'available' || v.status === 'reserved' || v.status === 'purchased'
      )
    : activeVehicles;

  if (activeVehicles.length === 0) {
    // No vehicles = perfect score
    return {
      score: {
        id: 'doorstroom',
        label: 'Doorstroom',
        score: 100,
        color: 'green',
      },
      explanations: [],
      nudges: [],
    };
  }

  // Calculate health for each vehicle (for SCORE - uses ALL vehicles)
  const vehicleHealths = activeVehicles.map((v) => calculateVehicleHealth(v, now, settings));

  // Count-weighted health (70%): simple average
  const countWeighted =
    vehicleHealths.reduce((sum, vh) => sum + vh.healthScore, 0) / vehicleHealths.length;

  // Value-weighted health (30%): weighted by capital locked
  const totalValueCents = vehicleHealths.reduce((sum, vh) => sum + vh.valueCents, 0);
  const valueWeighted =
    totalValueCents > 0
      ? vehicleHealths.reduce((sum, vh) => sum + vh.healthScore * vh.valueCents, 0) /
        totalValueCents
      : 100; // If no value data, assume perfect

  // Combined score
  const score = Math.round(
    countWeighted * DOORSTROOM_COUNT_WEIGHT + valueWeighted * DOORSTROOM_VALUE_WEIGHT
  );

  // Build explanations for unhealthy VISIBLE vehicles only (sorted by impact)
  // NO SLICING HERE - UI controls preview slicing when collapsed
  const visibleVehicleHealths = vehiclesForExplanations.map((v) =>
    calculateVehicleHealth(v, now, settings)
  );
  const unhealthyVehicles = visibleVehicleHealths
    .filter((vh) => vh.healthScore < 100)
    .sort((a, b) => b.impact - a.impact);

  const explanations: ExplanationItem[] = unhealthyVehicles.map((vh) => ({
    id: `doorstroom-${vh.vehicle.id}`,
    ringId: 'doorstroom',
    item: `${vh.vehicle.brand} ${vh.vehicle.model} (${vh.ageDays} dagen)`,
    impact: `-${Math.round(100 - vh.healthScore)}% Doorstroom`,
    impactValue: vh.impact,
    actionType: 'adjust_price',
    actionLabel: 'Prijs aanpassen',
    actionHref: `/vehicles?dialog=vehicle&mode=edit&id=${vh.vehicle.id}`,
    entityId: vh.vehicle.id,
    entityType: 'vehicle',
    // Include current vehicle status for inline status selector
    vehicleStatus: vh.vehicle.status as
      | 'available'
      | 'reserved'
      | 'sold'
      | 'purchased'
      | 'maintenance',
  }));

  // Build nudges for vehicles approaching stale threshold (within 48 hours)
  const { staleVehicleDays } = settings;
  const approachingStale = vehicleHealths.filter((vh) => {
    const daysUntilStale = staleVehicleDays - vh.ageDays;
    return daysUntilStale > 0 && daysUntilStale <= 2;
  });

  const nudges: PredictiveNudge[] = [];
  if (approachingStale.length > 0) {
    nudges.push({
      id: 'doorstroom-approaching-stale',
      ringId: 'doorstroom',
      message: `${approachingStale.length} voertuig${
        approachingStale.length > 1 ? 'en worden' : ' wordt'
      } oud binnen 48 uur`,
      count: approachingStale.length,
      daysUntil: 2,
      actionHref: '/vehicles?status=available',
    });
  }

  return {
    score: {
      id: 'doorstroom',
      label: 'Doorstroom',
      score,
      color: getRingColor(score),
    },
    explanations,
    nudges,
  };
}

// ============================================================================
// Vandaag (Flow) Calculation - v2
// ============================================================================

/**
 * Flow task item for Vandaag calculation
 */
interface FlowTaskItem {
  id: string;
  title: string;
  source:
    | 'ready_to_send' // Invoice marked ready to send
    | 'auto_follow_up' // Auto follow-up reminder (age-based)
    | 'auto_quote_follow_up' // Quote follow-up
    | 'manual'; // Manual team task
  dueAt: Date;
  isOverdue: boolean;
  isDueToday: boolean;
  isCompleted: boolean;
  isSnoozed: boolean;
  isDismissed: boolean;
  entityId?: string;
  entityType?: 'salesInvoice' | 'quote' | 'task';
  /** Task scope for manual tasks: 'team' or 'user' */
  taskScope?: 'team' | 'user';
  /** Can this item be snoozed? */
  canSnooze: boolean;
  /** Can this item be dismissed? */
  canDismiss: boolean;
  // Invoice contact data for reminder actions (Mail/WhatsApp)
  invoiceNo?: string;
  partyName?: string;
  partyEmail?: string;
  partyPhone?: string;
  partyId?: string;
}

/**
 * Check if an item is currently snoozed
 */
function isSnoozedToday(snoozedUntil?: string): boolean {
  if (!snoozedUntil) return false;
  const snoozeDate = new Date(snoozedUntil);
  return snoozeDate > new Date();
}

/**
 * Calculate Vandaag (Flow) ring score - v2
 *
 * Vandaag v2 consists of three sources:
 * 1. Daily Actions (70% weight):
 *    - Invoices marked "readyToSend"
 *    - Manual team tasks due today or overdue
 *    - Auto follow-up reminders (age-based)
 *
 * 2. Auto Follow-up Reminders:
 *    - After idealCollectionDays + 7 → show follow-up reminder
 *    - After idealCollectionDays + 14 → show again
 *    - Ephemeral: disappear next day, never appear once paid
 *
 * 3. Weekly Goals (30% weight):
 *    - Test drives, new leads, follow-ups completed
 *    - Progress bars visible, count toward score
 *    - Not snoozable
 *
 * Scoring (70/30 split):
 * - Daily Actions (70%): completed / (completed + open)
 * - Weekly Goals (30%): average progress across all goals
 * - Snoozed items count as "done today"
 * - Dismissed items are excluded from both numerator and denominator
 */
export function calculateVandaag(
  salesInvoices: SalesInvoiceForRing[],
  manualTasks: ManualTaskForRing[],
  settings: DashboardRingSettingsType,
  weeklyGoals?: WeeklyGoalsData,
  ringStateMap?: RingItemStateMap
): { score: RingScore; explanations: ExplanationItem[] } {
  const now = new Date();
  const today = startOfDay(now);

  const flowTasks: FlowTaskItem[] = [];

  // ========================================================================
  // 1. Ready-to-send invoices (replaces all draft invoices)
  // ========================================================================
  const readyToSendInvoices = salesInvoices.filter(
    (inv) => inv.readyToSend && inv.status === 'draft' && !inv.isQuote
  );

  for (const inv of readyToSendInvoices) {
    const isSnoozed = isSnoozedToday(inv.snoozedUntil);
    const isDismissed = inv.isDismissed === true;

    flowTasks.push({
      id: `ready-to-send-${inv.id}`,
      title: `Factuur ${inv.invoiceNo} versturen`,
      source: 'ready_to_send',
      dueAt: today,
      isOverdue: false,
      isDueToday: true,
      isCompleted: false,
      isSnoozed,
      isDismissed,
      entityId: inv.id,
      entityType: 'salesInvoice',
      canSnooze: true,
      canDismiss: true,
    });
  }

  // ========================================================================
  // 2. Auto follow-up reminders for unpaid invoices
  // ========================================================================
  // Eligible: sent OR overdue, NOT paid, NOT cancelled, NOT quote
  // Reminder shows every 7 days starting from idealCollectionDays
  // baseDate = sentAt ?? invoiceDate (fallback if sentAt missing)
  const unpaidInvoices = salesInvoices.filter(
    (inv) => (inv.status === 'sent' || inv.status === 'overdue') && !inv.isQuote
  );

  const { idealCollectionDays } = settings;

  // DEV logging for invoice reminders
  devLog('[Vandaag] Invoice reminder check:', {
    unpaidInvoicesTotal: unpaidInvoices.length,
    idealCollectionDays,
  });

  let remindersToday = 0;

  for (const inv of unpaidInvoices) {
    // Use invoiceDate as the base date (simplified per product owner request)
    // Users perceive invoice date as the send moment
    const baseDate = startOfDay(inv.invoiceDate);
    const daysSinceBase = differenceInDays(today, baseDate);
    const daysOver = daysSinceBase - idealCollectionDays;

    // Reminder shows when:
    // - daysOver >= 0 (past ideal collection days)
    // - daysOver % 7 === 0 (every 7 days: day 0, 7, 14, 21, ...)
    const shouldShowToday = daysOver >= 0 && daysOver % 7 === 0;

    // Calculate next reminder day for logging
    const nextReminderDays =
      daysOver < 0
        ? idealCollectionDays - daysSinceBase // days until first reminder
        : 7 - (daysOver % 7); // days until next 7-day cycle

    // DEV logging - only log invoices that are showing or close to showing
    if (import.meta.env.DEV && (shouldShowToday || Math.abs(daysOver) <= 7)) {
      devLog('[Vandaag] Invoice reminder:', {
        invoiceNo: inv.invoiceNo,
        invoiceDate: baseDate.toISOString().split('T')[0],
        ageDays: daysSinceBase,
        daysOver,
        shouldShow: shouldShowToday,
        nextIn: nextReminderDays,
      });
    }

    if (shouldShowToday) {
      remindersToday++;
      const isSnoozed = isSnoozedToday(inv.snoozedUntil);
      const isDismissed = inv.isDismissed === true;

      // Determine reminder message based on how overdue
      const reminderNumber = Math.floor(daysOver / 7) + 1;
      const title =
        daysOver === 0
          ? `Betaaltermijn bereikt: ${inv.invoiceNo}`
          : `Herinnering #${reminderNumber}: ${inv.invoiceNo} opvolgen`;

      flowTasks.push({
        id: `follow-up-${inv.id}-${daysOver}`,
        title,
        source: 'auto_follow_up',
        dueAt: today,
        isOverdue: daysOver > 0,
        isDueToday: true,
        isCompleted: false,
        isSnoozed,
        isDismissed,
        entityId: inv.id,
        entityType: 'salesInvoice',
        canSnooze: true,
        canDismiss: false, // Auto reminders can't be permanently dismissed
        // Invoice contact data for Mail/WhatsApp actions
        invoiceNo: inv.invoiceNo,
        partyName: inv.partyName,
        partyEmail: inv.partyEmail,
        partyPhone: inv.partyPhone,
        partyId: inv.partyId,
      });
    }
  }

  devLog('[Vandaag] Invoice reminders summary:', {
    eligibleInvoices: unpaidInvoices.length,
    remindersDueToday: remindersToday,
  });

  // ========================================================================
  // 3. Quote follow-up (sent quotes past follow-up days)
  // ========================================================================
  const sentQuotes = salesInvoices.filter(
    (inv) => inv.isQuote && inv.status === 'sent' && inv.sentAt && !inv.followUpDismissed
  );

  for (const quote of sentQuotes) {
    if (!quote.sentAt) continue;

    const followUpDueDate = new Date(quote.sentAt);
    followUpDueDate.setDate(followUpDueDate.getDate() + settings.quoteFollowUpDays);

    const isOverdue = followUpDueDate < today;
    const isDueToday = !isOverdue && differenceInDays(followUpDueDate, today) === 0;

    if (isOverdue || isDueToday) {
      const isSnoozed = isSnoozedToday(quote.snoozedUntil);
      const isDismissed = quote.isDismissed === true;

      flowTasks.push({
        id: `quote-follow-up-${quote.id}`,
        title: `Offerte ${quote.invoiceNo} opvolgen`,
        source: 'auto_quote_follow_up',
        dueAt: followUpDueDate,
        isOverdue,
        isDueToday,
        isCompleted: false,
        isSnoozed,
        isDismissed,
        entityId: quote.id,
        entityType: 'quote',
        canSnooze: true,
        canDismiss: true,
      });
    }
  }

  // ========================================================================
  // 4. Manual team tasks
  // ========================================================================
  for (const task of manualTasks) {
    if (!task.dueAt) continue;

    const taskDueDate = startOfDay(task.dueAt);
    const isOverdue = taskDueDate < today && task.status !== 'done';
    const isDueToday = differenceInDays(taskDueDate, today) === 0;
    const isCompleted = task.status === 'done';
    const isSnoozed = isSnoozedToday(task.snoozedUntil);
    const isDismissed = task.isDismissed === true;

    // Only include if due today or overdue
    if (isOverdue || isDueToday) {
      flowTasks.push({
        id: `manual-${task.id}`,
        title: task.title,
        source: 'manual',
        dueAt: task.dueAt,
        isOverdue,
        isDueToday,
        isCompleted,
        isSnoozed,
        isDismissed,
        entityId: task.id,
        entityType: 'task',
        taskScope: task.scope, // Pass scope for proper key generation
        canSnooze: true,
        canDismiss: true,
      });
    }
  }

  // ========================================================================
  // Calculate score (70% daily actions, 30% weekly goals)
  // ========================================================================

  // Helper to get ringStateMap key for each flow task
  const getFlowTaskKey = (task: FlowTaskItem): string => {
    if (task.source === 'manual' && task.entityId) {
      // Manual tasks use getTaskKey with actual scope from task
      const scope = task.taskScope || 'team'; // Default to team if not specified
      return getTaskKey(task.entityId, scope);
    }
    if (task.source === 'auto_follow_up' && task.entityId) {
      return getInvoiceReminderKey(task.entityId);
    }
    if (task.source === 'auto_quote_follow_up' && task.entityId) {
      return getQuoteFollowUpKey(task.entityId);
    }
    if (task.source === 'ready_to_send' && task.entityId) {
      // Ready-to-send uses invoice reminder key
      return getInvoiceReminderKey(task.entityId);
    }
    return `unknown:${task.id}`;
  };

  // Check if item is hidden via ringStateMap (snoozed/muted via UI)
  const isHiddenViaRingState = (task: FlowTaskItem): boolean => {
    if (!ringStateMap) return false;
    const key = getFlowTaskKey(task);
    const state = ringStateMap.get(key);
    return isItemHidden(state, now);
  };

  // For score calculation:
  // - "handled" = completed OR hidden via ringStateMap (snoozed/dismissed in UI)
  // - "total" = all items (not permanently dismissed)
  const allItems = flowTasks.filter((t) => !t.isDismissed);

  const handledCount = allItems.filter((t) => t.isCompleted || isHiddenViaRingState(t)).length;

  const totalCount = allItems.length;

  // Calculate daily actions score (0-100)
  let dailyActionsScore: number;
  if (totalCount === 0) {
    dailyActionsScore = 100; // No tasks = perfect
  } else {
    dailyActionsScore = Math.round((handledCount / totalCount) * 100);
  }

  // For explanations: only show items that are NOT completed AND NOT hidden
  const visibleItems = allItems.filter((t) => !t.isCompleted && !isHiddenViaRingState(t));

  // DEV logging for Vandaag score calculation
  devLog('[Vandaag] Score calculation:', {
    flowTasksCount: flowTasks.length,
    totalCount,
    handledCount,
    visibleCount: visibleItems.length,
    dailyActionsScore,
    ringStateMapSize: ringStateMap?.size || 0,
    itemDetails: flowTasks.map((t) => ({
      id: t.id,
      title: t.title.substring(0, 30),
      key: getFlowTaskKey(t),
      isCompleted: t.isCompleted,
      isHiddenViaRingState: isHiddenViaRingState(t),
      isDismissed: t.isDismissed,
      source: t.source,
    })),
  });

  // Calculate weekly goals score (0-100)
  let weeklyGoalsScore = 100; // Default to 100 if no goals set

  if (weeklyGoals) {
    const goalScores: number[] = [];

    // Test drives progress
    if (weeklyGoals.testDrivesTarget > 0) {
      const progress = Math.min(weeklyGoals.testDrivesCompleted / weeklyGoals.testDrivesTarget, 1);
      goalScores.push(progress * 100);
    }

    // New leads progress
    if (weeklyGoals.newLeadsTarget > 0) {
      const progress = Math.min(weeklyGoals.newLeadsCount / weeklyGoals.newLeadsTarget, 1);
      goalScores.push(progress * 100);
    }

    // Follow-ups progress
    if (weeklyGoals.followUpsTarget > 0) {
      const progress = Math.min(weeklyGoals.followUpsCompleted / weeklyGoals.followUpsTarget, 1);
      goalScores.push(progress * 100);
    }

    // Average of all goal scores
    if (goalScores.length > 0) {
      weeklyGoalsScore = Math.round(goalScores.reduce((a, b) => a + b, 0) / goalScores.length);
    }
  }

  // Combined score: 70% daily actions + 30% weekly goals
  // If no weekly goals are configured, use 100% daily actions
  let score: number;
  if (weeklyGoals) {
    score = Math.round(dailyActionsScore * 0.7 + weeklyGoalsScore * 0.3);
  } else {
    score = dailyActionsScore;
  }

  // DEV logging for Vandaag score breakdown
  devLog('[Vandaag] Score breakdown:', {
    flowTasksTotal: flowTasks.length,
    totalCount,
    handledCount,
    visibleCount: visibleItems.length,
    dailyActionsScore,
    weeklyGoalsScore,
    finalScore: score,
  });

  // Log each visible task for debugging
  if (import.meta.env.DEV && visibleItems.length > 0) {
    devLog(
      '[Vandaag] Visible tasks:',
      visibleItems.map((t) => ({
        id: t.id,
        title: t.title,
        source: t.source,
        isDueToday: t.isDueToday,
        isOverdue: t.isOverdue,
      }))
    );
  }

  // ========================================================================
  // Build explanations for visible tasks (overdue first, then due today)
  // ========================================================================
  const sortedVisibleTasks = visibleItems.sort((a, b) => {
    // Overdue tasks first
    if (a.isOverdue && !b.isOverdue) return -1;
    if (!a.isOverdue && b.isOverdue) return 1;
    return 0;
  });

  const explanations: ExplanationItem[] = sortedVisibleTasks.map((task) => {
    let actionType: ExplanationItem['actionType'] = 'view_invoice';
    let actionLabel = 'Bekijken';
    let actionHref = '/';

    if (task.source === 'ready_to_send') {
      actionType = 'send_invoice';
      actionLabel = 'Versturen';
      actionHref = `/sales-invoices?dialog=salesInvoice&mode=edit&id=${task.entityId}`;
    } else if (task.source === 'auto_follow_up') {
      actionType = 'send_reminder';
      actionLabel = 'Herinnering sturen';
      actionHref = `/sales-invoices?dialog=salesInvoice&mode=view&id=${task.entityId}`;
    } else if (task.source === 'auto_quote_follow_up') {
      actionType = 'follow_up';
      actionLabel = 'Opvolgen';
      actionHref = `/sales-invoices?dialog=salesInvoice&mode=view&id=${task.entityId}`;
    } else if (task.source === 'manual') {
      actionType = 'view_invoice'; // TODO: add 'view_task' action type
      actionLabel = 'Bekijken';
      actionHref = `/tasks?id=${task.entityId}`;
    }

    return {
      id: task.id,
      ringId: 'vandaag' as const,
      item: task.title,
      impact: task.isOverdue ? 'Achterstallig' : 'Vandaag',
      impactValue: task.isOverdue ? 100 : 50,
      actionType,
      actionLabel,
      actionHref,
      entityId: task.entityId || task.id,
      entityType: task.entityType || 'salesInvoice',
      canSnooze: task.canSnooze,
      canDismiss: task.canDismiss,
      taskScope: task.taskScope, // Pass scope for correct key generation in UI
      // Invoice contact data for Mail/WhatsApp actions (auto_follow_up items)
      invoiceNo: task.invoiceNo,
      partyName: task.partyName,
      partyEmail: task.partyEmail,
      partyPhone: task.partyPhone,
      partyId: task.partyId,
    };
  });

  return {
    score: {
      id: 'vandaag',
      label: 'Vandaag',
      score,
      color: getRingColor(score),
    },
    explanations,
  };
}

// ============================================================================
// Liquiditeit (Liquidity) Calculation
// ============================================================================

/**
 * Liquiditeit v2 Scoring Model
 *
 * CORE PRINCIPLE: Only invoices overdue BEYOND idealCollectionDays count.
 * Invoices within idealCollectionDays are "business as usual" and don't penalize.
 *
 * SCORING MODEL:
 * 1. Exposure Score (60%): How much overdue money vs inventory value
 *    - exposurePct = overdueOutstandingCents / inventoryValueCents
 *    - <= 3%: 100
 *    - 3-6%: linear 100 → 70
 *    - 6-10%: linear 70 → 40
 *    - >= 10%: 0
 *
 * 2. Aging Score (40%): How old are overdue invoices (amount-weighted)
 *    - daysOver 1-7: bucket score 70
 *    - daysOver 8-14: bucket score 40
 *    - daysOver 15+: bucket score 0
 *    - agingScore = sum(bucketScore * totalCents) / sum(totalCents)
 *
 * FINAL: round(exposureScore * 0.6 + agingScore * 0.4)
 *
 * COLOR THRESHOLDS:
 * - green: >= 70
 * - orange: 40-69
 * - red: < 40
 *
 * TWEAK POINTS:
 * - idealCollectionDays: in DashboardRingSettings
 * - Exposure thresholds: EXPOSURE_THRESHOLDS below
 * - Aging buckets: AGING_BUCKETS below
 * - Exposure/aging weights: LIQUIDITEIT_EXPOSURE_WEIGHT / LIQUIDITEIT_AGING_WEIGHT
 */

const LIQUIDITEIT_EXPOSURE_WEIGHT = 0.6;
const LIQUIDITEIT_AGING_WEIGHT = 0.4;

/** Exposure thresholds (as decimals, not percentages) */
const EXPOSURE_THRESHOLDS = {
  low: 0.03, // <= 3%: score 100
  medium: 0.06, // 3-6%: linear 100 → 70
  high: 0.1, // 6-10%: linear 70 → 40, >= 10%: 0
};

/** Aging bucket scores based on daysOver */
const AGING_BUCKETS = {
  week1: { maxDays: 7, score: 70 },
  week2: { maxDays: 14, score: 40 },
  beyond: { score: 0 },
};

interface OverdueInvoice {
  invoice: SalesInvoiceForRing;
  ageDays: number;
  daysOver: number;
  bucketScore: number;
  impact: number;
}

/**
 * Get aging bucket score for an invoice based on days overdue
 */
function getAgingBucketScore(daysOver: number): number {
  if (daysOver <= 0) return 100; // Not overdue
  if (daysOver <= AGING_BUCKETS.week1.maxDays) return AGING_BUCKETS.week1.score;
  if (daysOver <= AGING_BUCKETS.week2.maxDays) return AGING_BUCKETS.week2.score;
  return AGING_BUCKETS.beyond.score;
}

/**
 * Calculate exposure score based on overdue percentage of inventory
 */
function calculateExposureScore(exposurePct: number): number {
  if (exposurePct <= EXPOSURE_THRESHOLDS.low) {
    return 100;
  }
  if (exposurePct <= EXPOSURE_THRESHOLDS.medium) {
    // Linear 100 → 70 between 3% and 6%
    const range = EXPOSURE_THRESHOLDS.medium - EXPOSURE_THRESHOLDS.low;
    const over = exposurePct - EXPOSURE_THRESHOLDS.low;
    return Math.round(100 - (over / range) * 30);
  }
  if (exposurePct <= EXPOSURE_THRESHOLDS.high) {
    // Linear 70 → 40 between 6% and 10%
    const range = EXPOSURE_THRESHOLDS.high - EXPOSURE_THRESHOLDS.medium;
    const over = exposurePct - EXPOSURE_THRESHOLDS.medium;
    return Math.round(70 - (over / range) * 30);
  }
  // >= 10%: score 0
  return 0;
}

/**
 * Calculate Liquiditeit (Liquidity) ring score
 * Uses exposure + aging model where only overdue invoices (beyond idealCollectionDays) count
 *
 * IMPORTANT: Liquidity is a "truth metric" - the score must NOT change based on ringItemState.
 * - allInvoices: Used for score calculation (unfiltered - the objective financial truth)
 * - visibleInvoices: Used for explanations list (filtered by ringItemState for UI)
 */
export function calculateLiquiditeit(
  allInvoices: SalesInvoiceForRing[],
  settings: DashboardRingSettingsType,
  inventoryValueCents: number = 0,
  visibleInvoices?: SalesInvoiceForRing[]
): { score: RingScore; explanations: ExplanationItem[]; nudges: PredictiveNudge[] } {
  // Use visibleInvoices for explanations if provided, otherwise use allInvoices
  const invoicesForExplanations = visibleInvoices ?? allInvoices;
  const now = startOfDay(new Date());
  const { idealCollectionDays } = settings;

  // Filter to sent invoices that are not paid (and not quotes)
  // IMPORTANT: Snoozed invoices are EXCLUDED from score calculation
  // This is intentional - snoozing means "I handled it" and should improve the score temporarily
  const nowDate = new Date();
  const unpaidInvoices = allInvoices.filter((inv) => {
    // Basic filter: sent/overdue and not a quote
    if (!((inv.status === 'sent' || inv.status === 'overdue') && !inv.isQuote)) {
      return false;
    }
    // EXCLUDE invoices with active reminder snooze from SCORE calculation
    // This makes the score go UP when dealer snoozes (they "handled" it)
    // After snooze expires, invoice contributes to score again
    if (inv.reminderSnoozedUntil && inv.reminderSnoozedUntil > nowDate) {
      return false;
    }
    return true;
  });

  // For explanations, use the same filtering (snoozed = excluded)
  const unpaidInvoicesForExplanations = invoicesForExplanations.filter((inv) => {
    // Basic filter: sent/overdue and not a quote
    if (!((inv.status === 'sent' || inv.status === 'overdue') && !inv.isQuote)) {
      return false;
    }
    // Exclude invoices with active reminder snooze
    if (inv.reminderSnoozedUntil && inv.reminderSnoozedUntil > nowDate) {
      return false;
    }
    return true;
  });

  // Calculate daysOver for each invoice and filter to only overdue ones
  const overdueInvoices: OverdueInvoice[] = [];
  const approachingOverdue: SalesInvoiceForRing[] = [];

  for (const invoice of unpaidInvoices) {
    const sentDate = invoice.sentAt || invoice.invoiceDate;
    const ageDays = differenceInDays(now, sentDate);
    const daysOver = Math.max(0, ageDays - idealCollectionDays);

    if (daysOver > 0) {
      // This invoice is overdue
      const bucketScore = getAgingBucketScore(daysOver);
      const impact = invoice.totalCents * (100 - bucketScore);
      overdueInvoices.push({ invoice, ageDays, daysOver, bucketScore, impact });
    } else {
      // Check if approaching overdue (within 2 days)
      const daysUntilOverdue = idealCollectionDays - ageDays;
      if (daysUntilOverdue > 0 && daysUntilOverdue <= 2) {
        approachingOverdue.push(invoice);
      }
    }
  }

  // If no overdue invoices, perfect score
  if (overdueInvoices.length === 0) {
    const nudges: PredictiveNudge[] = [];
    if (approachingOverdue.length > 0) {
      nudges.push({
        id: 'liquiditeit-approaching-due',
        ringId: 'liquiditeit',
        message: `${approachingOverdue.length} factuur${
          approachingOverdue.length > 1 ? 'en overschrijden' : ' overschrijdt'
        } binnenkort je betaaltermijn`,
        count: approachingOverdue.length,
        daysUntil: 2,
        actionHref: '/sales-invoices?status=sent',
      });
    }
    return {
      score: {
        id: 'liquiditeit',
        label: 'Liquiditeit',
        score: 100,
        color: 'green',
      },
      explanations: [],
      nudges,
    };
  }

  // Step 1: Calculate overdue outstanding
  const overdueOutstandingCents = overdueInvoices.reduce(
    (sum, oi) => sum + oi.invoice.totalCents,
    0
  );

  // Step 2: Calculate exposure score
  let exposureScore: number;
  if (inventoryValueCents <= 0) {
    // No inventory value data - can't calculate exposure, assume moderate risk
    exposureScore = 70;
    devWarn('[Liquiditeit] No inventory value available for exposure calculation');
  } else {
    const exposurePct = overdueOutstandingCents / inventoryValueCents;
    exposureScore = calculateExposureScore(exposurePct);
  }

  // Step 3: Calculate aging score (amount-weighted average of bucket scores)
  const totalOverdueAmount = overdueInvoices.reduce((sum, oi) => sum + oi.invoice.totalCents, 0);
  const agingScore =
    totalOverdueAmount > 0
      ? overdueInvoices.reduce((sum, oi) => sum + oi.bucketScore * oi.invoice.totalCents, 0) /
        totalOverdueAmount
      : 100;

  // Step 4: Combine scores
  const score = Math.round(
    exposureScore * LIQUIDITEIT_EXPOSURE_WEIGHT + agingScore * LIQUIDITEIT_AGING_WEIGHT
  );

  // DEV logging for Liquiditeit formula transparency
  if (import.meta.env.DEV) {
    const exposurePct =
      inventoryValueCents > 0
        ? ((overdueOutstandingCents / inventoryValueCents) * 100).toFixed(2) + '%'
        : 'N/A (no inventory)';

    devLog('[Liquiditeit] Formula breakdown:', {
      inventoryValueCents,
      inventoryValueEur: (inventoryValueCents / 100).toFixed(2),
      overdueOutstandingCents,
      overdueOutstandingEur: (overdueOutstandingCents / 100).toFixed(2),
      exposurePct,
      exposureScore,
      agingScore: Math.round(agingScore),
      weights: { exposure: LIQUIDITEIT_EXPOSURE_WEIGHT, aging: LIQUIDITEIT_AGING_WEIGHT },
      finalScore: score,
    });

    devLog('[Liquiditeit] Invoice counts:', {
      unpaidTotal: unpaidInvoices.length,
      overdueCount: overdueInvoices.length,
      approachingOverdueCount: approachingOverdue.length,
    });

    // Log each overdue invoice for debugging
    if (overdueInvoices.length > 0) {
      devLog(
        '[Liquiditeit] Overdue invoices:',
        overdueInvoices.map((oi) => ({
          invoiceNo: oi.invoice.invoiceNo,
          totalEur: (oi.invoice.totalCents / 100).toFixed(2),
          daysOver: oi.daysOver,
          bucketScore: oi.bucketScore,
        }))
      );
    }
  }

  // Build explanations for VISIBLE overdue invoices only (filtered by ringItemState)
  // Create a Set of visible invoice IDs for fast lookup
  const visibleInvoiceIds = new Set(unpaidInvoicesForExplanations.map((inv) => inv.id));

  // Filter overdueInvoices to only include visible ones for explanations
  const visibleOverdueInvoices = overdueInvoices.filter((oi) =>
    visibleInvoiceIds.has(oi.invoice.id)
  );

  // NO SLICING HERE - UI controls preview slicing when collapsed
  const sortedOverdue = visibleOverdueInvoices.sort((a, b) => b.impact - a.impact);

  const explanations: ExplanationItem[] = sortedOverdue.map((oi) => {
    const amount = (oi.invoice.totalCents / 100).toLocaleString('nl-NL', {
      style: 'currency',
      currency: 'EUR',
    });

    return {
      id: `liquiditeit-${oi.invoice.id}`,
      ringId: 'liquiditeit',
      item: `Factuur ${oi.invoice.invoiceNo} (${amount})`,
      impact: `${oi.daysOver} dagen over termijn`,
      impactValue: oi.impact,
      actionType: 'send_reminder',
      actionLabel: 'Herinnering sturen',
      actionHref: `/sales-invoices?dialog=salesInvoice&mode=view&id=${oi.invoice.id}`,
      entityId: oi.invoice.id,
      entityType: 'salesInvoice',
      // Include party data for email/WhatsApp flows
      partyEmail: oi.invoice.partyEmail,
      partyPhone: oi.invoice.partyPhone,
      partyName: oi.invoice.partyName,
      partyId: oi.invoice.partyId,
      invoiceNo: oi.invoice.invoiceNo,
    };
  });

  // Build nudges for invoices approaching overdue
  const nudges: PredictiveNudge[] = [];
  if (approachingOverdue.length > 0) {
    // For single invoice: deep-link to that specific invoice dialog
    // For multiple: link to filtered list
    const firstInvoice = approachingOverdue[0];
    const isSingle = approachingOverdue.length === 1 && firstInvoice;

    nudges.push({
      id: 'liquiditeit-approaching-due',
      ringId: 'liquiditeit',
      message: `${approachingOverdue.length} factuur${
        approachingOverdue.length > 1 ? 'en overschrijden' : ' overschrijdt'
      } binnenkort je betaaltermijn`,
      count: approachingOverdue.length,
      daysUntil: 2,
      // Deep-link to specific invoice when single, otherwise filter list
      actionHref: isSingle
        ? `/sales-invoices?dialog=salesInvoice&mode=view&id=${firstInvoice.id}`
        : '/sales-invoices?status=sent',
      // Include entityId for programmatic access
      entityId: isSingle ? firstInvoice.id : undefined,
      entityType: isSingle ? 'salesInvoice' : undefined,
    });
  }

  return {
    score: {
      id: 'liquiditeit',
      label: 'Liquiditeit',
      score,
      color: getRingColor(score),
    },
    explanations,
    nudges,
  };
}

// ============================================================================
// Combined Calculation
// ============================================================================

/**
 * Calculate all three ring scores
 *
 * Ring state filtering is applied BEFORE score calculation for ALL rings.
 * This ensures the UI updates instantly when items are snoozed/muted.
 */
export function calculateAllRings(input: RingCalculationInput): RingCalculationResult {
  const {
    vehicles,
    salesInvoices,
    manualTasks,
    settings,
    inventoryValueCents,
    weeklyGoals,
    ringStateMap,
  } = input;
  const now = new Date();

  // ========================================================================
  // PRE-FILTER: Apply ring state filtering BEFORE score calculation
  // ========================================================================

  // Filter vehicles based on ring state (doorstroom)
  const filteredVehicles = ringStateMap
    ? vehicles.filter((v) => {
        const key = getDoorstroomKey(v.id);
        const state = ringStateMap.get(key);
        return !isItemHidden(state, now);
      })
    : vehicles;

  // Filter invoices based on ring state (liquiditeit + invoice reminders + quote follow-ups)
  const filteredInvoices = ringStateMap
    ? salesInvoices.filter((inv) => {
        const liquiditeitKey = getLiquiditeitKey(inv.id);
        const reminderKey = getInvoiceReminderKey(inv.id);
        const quoteKey = getQuoteFollowUpKey(inv.id);
        const liquiditeitState = ringStateMap.get(liquiditeitKey);
        const reminderState = ringStateMap.get(reminderKey);
        const quoteState = ringStateMap.get(quoteKey);
        // Hidden if any of the states says so
        return (
          !isItemHidden(liquiditeitState, now) &&
          !isItemHidden(reminderState, now) &&
          !isItemHidden(quoteState, now)
        );
      })
    : salesInvoices;

  // Filter tasks based on ring state
  // Uses centralized getTaskKey helper for consistent key generation
  const filteredTasks = ringStateMap
    ? manualTasks.filter((task) => {
        const key = getTaskKey(task.id, task.scope === 'team' ? 'team' : 'user');
        const state = ringStateMap.get(key);
        return !isItemHidden(state, now);
      })
    : manualTasks;

  // DEV logging for ring state filtering
  devLog('[calculateAllRings] Filtering summary:', {
    ringStateMapSize: ringStateMap?.size || 0,
    vehiclesTotal: vehicles.length,
    vehiclesFiltered: filteredVehicles.length,
    invoicesTotal: salesInvoices.length,
    invoicesFiltered: filteredInvoices.length,
    tasksTotal: manualTasks.length,
    tasksFiltered: filteredTasks.length,
    taskKeys: manualTasks.map((t) => ({
      id: t.id,
      scope: t.scope,
      key: getTaskKey(t.id, t.scope === 'team' ? 'team' : 'user'),
      hidden: ringStateMap
        ? isItemHidden(
            ringStateMap.get(getTaskKey(t.id, t.scope === 'team' ? 'team' : 'user')),
            now
          )
        : false,
    })),
  });

  // ========================================================================
  // CALCULATE SCORES
  // ========================================================================

  // Doorstroom: TRUTH METRIC - score uses ALL vehicles, explanations use filtered
  // This ensures muting/snoozing only hides list items, NOT the score
  const doorstroomResult = calculateDoorstroom(
    vehicles, // ALL vehicles for score calculation (truth)
    settings,
    filteredVehicles // Filtered vehicles for explanations only
  );

  // Liquiditeit: TRUTH METRIC - score uses ALL invoices, explanations use filtered
  // This ensures muting/snoozing only hides list items, NOT the score
  const liquiditeitResult = calculateLiquiditeit(
    salesInvoices, // ALL invoices for score calculation (truth)
    settings,
    inventoryValueCents,
    filteredInvoices // Filtered invoices for explanations only
  );

  // Vandaag: ACTION METRIC - uses ALL data, ringStateMap determines what's "handled"
  // Hidden items (snoozed/muted) count as "handled" for the score
  // Only non-hidden items appear in explanations
  const vandaagResult = calculateVandaag(
    salesInvoices,
    manualTasks,
    settings,
    weeklyGoals,
    ringStateMap
  );

  // ========================================================================
  // COMBINE EXPLANATIONS (already filtered by pre-filtered inputs)
  // ========================================================================

  const allExplanations = [
    ...doorstroomResult.explanations,
    ...vandaagResult.explanations,
    ...liquiditeitResult.explanations,
  ];

  const allNudges = [...doorstroomResult.nudges, ...liquiditeitResult.nudges];

  return {
    rings: {
      doorstroom: doorstroomResult.score,
      vandaag: vandaagResult.score,
      liquiditeit: liquiditeitResult.score,
    },
    explanations: allExplanations,
    nudges: allNudges,
  };
}
